import { State } from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridItemChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { FC, PropsWithChildren, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import usePostMessage, { handlePostMessage } from 'src/hooks/usePostMessage';
import UnsavedSelectionDialog from 'src/sections/catalog_products/UnsavedSelectionDialog';
import useUnsavedSelection from 'src/sections/catalog_products/useUnsavedSelection';
import useUpdateCartMutation from 'src/services/cart/useUpdateCartMutation';
import useAddToFavoritesMutation from 'src/services/favorite/useAddToFavoritesMutation';
import useGetFavoriteFoldersQuery from 'src/services/favorite/useGetFavoriteFoldersQuery';
import { CartItem } from 'src/types/cart';
import { ProductSelections } from 'src/types/catalog';
import { getCartItemProductKey, removeProductSelection } from 'src/utils/productActionMappers';
import { createContext, useContextSelector } from 'use-context-selector';
import { assertSearchProductAncestor, getFavoritesErrorMessage } from './searchProductContext.guards';
import {
  SearchProductSelectionState,
  SearchProductSelectionsContextShape,
} from './searchProductContext.types';
import {
  getHasSelectedItems,
  getIsDirty,
  getProductSelectionValue,
  getSelectedProductKeys,
  getShouldShowUnsavedModal,
  mapProductsToFavoriteItems,
  mapSelectionsToCartItems,
  resolveSearchSortOnViewChange,
} from './searchProductContext.utils';
import { SearchProductDetailsContext } from './SearchProductDetailsContext';
import { SearchProductFiltersContext } from './SearchProductFiltersContext';
import { SearchProductQueryContext } from './SearchProductQueryContext';

export const SearchProductSelectionsContext = createContext<SearchProductSelectionsContextShape>(
  {} as SearchProductSelectionsContextShape
);

export const SearchProductSelectionsProvider: FC<PropsWithChildren> = ({
  children,
}): ReactElement => {
  const { t } = useTranslation();
  const [productSelections, setProductSelections] = useState<ProductSelections>({});

  const dataState = useContextSelector(SearchProductFiltersContext, (context) => context.dataState);
  const setDataState = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setDataState
  );
  const isSupplierUser = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.isSupplierUser
  );
  assertSearchProductAncestor(
    setDataState,
    'SearchProductFiltersProvider',
    'SearchProductSelectionsProvider'
  );
  const setRefineSearch = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setRefineSearch
  );
  const selectedView = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.selectedView
  );
  const setSelectedView = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setSelectedView
  );
  const listViewTake = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.listViewTake
  );
  const pictureViewTake = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.pictureViewTake
  );
  const setListViewTake = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setListViewTake
  );
  const setPictureViewTake = useContextSelector(
    SearchProductFiltersContext,
    (context) => context.setPictureViewTake
  );

  const products = useContextSelector(SearchProductQueryContext, (context) => context.products);
  const fetchProducts = useContextSelector(
    SearchProductQueryContext,
    (context) => context.fetchProducts
  );
  assertSearchProductAncestor(
    fetchProducts,
    'SearchProductQueryProvider',
    'SearchProductSelectionsProvider'
  );

  const selectedProduct = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.selectedProduct
  );
  const isProductPanelOpen = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.isProductPanelOpen
  );
  const handleCloseProductPanel = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.handleCloseProductPanel
  );
  const fetchProductDetails = useContextSelector(
    SearchProductDetailsContext,
    (context) => context.fetchProductDetails
  );

  const isDirty = useMemo(() => getIsDirty(productSelections), [productSelections]);
  const hasSelectedItems = useMemo(
    () => getHasSelectedItems(productSelections),
    [productSelections]
  );

  usePostMessage({ isDirty });

  useEffect(() => {
    const beforeUnloadFunction = (event: BeforeUnloadEvent): string => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    if (isDirty) {
      window.addEventListener('beforeunload', beforeUnloadFunction);
    } else {
      window.removeEventListener('beforeunload', beforeUnloadFunction);
    }
    return () => window.removeEventListener('beforeunload', beforeUnloadFunction);
  }, [isDirty]);

  const getProductSelection = useCallback(
    (productKey: string): SearchProductSelectionState =>
      getProductSelectionValue(productSelections, productKey),
    [productSelections]
  );

  const updateProductSelection = useCallback(
    (productKey: string, updates: Partial<SearchProductSelectionState>) => {
      setProductSelections((prev) => {
        const current = prev[productKey] || { selected: false, qty: 0 };
        return {
          ...prev,
          [productKey]: { ...current, ...updates },
        };
      });
    },
    []
  );

  const clearSelections = useCallback(() => {
    setProductSelections({});
  }, []);

  const { mutateAsync: updateCart, isPending: isUpdatingCart } = useUpdateCartMutation({
    onSuccess: (_, request) => {
      toast.success(t('searchProductsAddedToCartSuccess', { count: request.items.length }), {
        autoClose: 3000,
      });
    },
    onError: () => {
      toast.error(t('searchProductsAddToCartError'));
    },
  });

  const handleSelectedItemsAddToCart = useCallback(async () => {
    const items = mapSelectionsToCartItems(productSelections, products);
    await updateCart({ items });
    handlePostMessage({ cartUpdated: true });
    clearSelections();
    await fetchProducts();
  }, [productSelections, products, updateCart, clearSelections, fetchProducts]);

  const {
    isOpen: isUnsavedSelectionOpen,
    handleConfirm: handleUnsavedConfirm,
    handleAddToCart: handleUnsavedAddToCart,
    handleClose: handleUnsavedClose,
    confirm: confirmUnsavedSelection,
    selectedItemsCount,
  } = useUnsavedSelection({
    productSelections,
    onAddToCart: handleSelectedItemsAddToCart,
  });

  const handleUnsavedClearAndConfirm = useCallback(async () => {
    clearSelections();
    await handleUnsavedConfirm();
  }, [clearSelections, handleUnsavedConfirm]);

  const handleConfirm = useCallback(
    async (callback: () => void | Promise<void>): Promise<void> => {
      await confirmUnsavedSelection(callback);
    },
    [confirmUnsavedSelection]
  );

  const handleRefineSearchChange = useCallback(
    async (value: string) => {
      await handleConfirm(() => {
        setDataState((prev) => ({ ...prev, skip: 0 }));
        setRefineSearch(value);
      });
    },
    [handleConfirm, setDataState, setRefineSearch]
  );

  const handleViewChange = useCallback(
    async (viewIndex: number): Promise<void> => {
      const targetTake = viewIndex === 0 ? listViewTake : pictureViewTake;

      const applyViewChange = () => {
        setSelectedView(viewIndex);
        setDataState((prev) => ({
          ...prev,
          skip: 0,
          take: targetTake,
          sort: resolveSearchSortOnViewChange(prev.sort, viewIndex),
        }));
      };

      if (
        getShouldShowUnsavedModal({
          productSelections,
          products,
          skip: dataState.skip,
          take: dataState.take,
        })
      ) {
        await handleConfirm(() => {
          clearSelections();
          applyViewChange();
        });
      } else {
        applyViewChange();
      }
    },
    [
      productSelections,
      products,
      dataState.skip,
      dataState.take,
      handleConfirm,
      clearSelections,
      listViewTake,
      pictureViewTake,
      setSelectedView,
      setDataState,
    ]
  );

  const onDataStateChange = useCallback(
    async (event: GridDataStateChangeEvent | State) => {
      const newDataState = 'dataState' in event ? event.dataState : event;
      await handleConfirm(() => {
        handleCloseProductPanel();
        setProductSelections({});
        if (newDataState) {
          setDataState((prev) => {
            const updated = { ...prev, ...newDataState };
            if (newDataState.take !== undefined && newDataState.take !== prev.take) {
              if (selectedView === 0) {
                setListViewTake(newDataState.take);
              } else {
                setPictureViewTake(newDataState.take);
              }
            }
            return updated;
          });
        }
      });
    },
    [handleConfirm, handleCloseProductPanel, selectedView, setDataState, setListViewTake, setPictureViewTake]
  );

  const onSelectionChange = useCallback((event: GridSelectionChangeEvent) => {
    const productKey = event?.dataItem?.productKey;
    if (!productKey) return;

    setProductSelections((prev) => {
      const current = prev[productKey] || { selected: false, qty: 0 };
      const newSelected = !current.selected;
      return {
        ...prev,
        [productKey]: {
          selected: newSelected,
          qty: newSelected && current.qty === 0 ? 1 : newSelected ? current.qty : 0,
        },
      };
    });
  }, []);

  const onItemChange = useCallback((event: GridItemChangeEvent) => {
    const { dataItem, field, value } = event;
    const productKey = dataItem.productKey as string;
    if (field === 'qty') {
      const qty = Number(value) || 0;
      setProductSelections((prev) => ({
        ...prev,
        [productKey]: {
          selected: qty !== 0,
          qty,
        },
      }));
    }
  }, []);

  const { mutateAsync: addToFavorites, isPending: isAddingToFavorites } = useAddToFavoritesMutation(
    {}
  );
  const { data: favoriteFolders = [] } = useGetFavoriteFoldersQuery(!isSupplierUser);
  const folderOptions = useMemo(
    () =>
      favoriteFolders.map((folder) => ({
        id: folder.id,
        name: folder.description,
      })),
    [favoriteFolders]
  );

  const handleSingleItemAddToCart = useCallback(
    async (item: CartItem) => {
      await updateCart({
        items: [item],
      });
      handlePostMessage({ cartUpdated: true });
      // Only the added item is deselected; the toolbar bulk add is what clears everything.
      setProductSelections((prev) => removeProductSelection(prev, getCartItemProductKey(item)));
      await fetchProducts();
    },
    [updateCart, fetchProducts]
  );

  const handleAddToFavorites = useCallback(
    async (folderId: number) => {
      try {
        const selectedItems = getSelectedProductKeys(productSelections);
        const selectedProducts =
          isProductPanelOpen && selectedProduct
            ? [selectedProduct]
            : products.filter((product) =>
                selectedItems.some((productKey) => productKey === product.productKey)
              );

        if (selectedProducts.length === 0) {
          toast.error(t('Please select at least one item to Add to Shopping List'));
          return;
        }

        const folder = folderOptions.find((option) => option.id === folderId);
        const folderName = folder?.name || 'folder';

        await addToFavorites({
          favouriteProduct: mapProductsToFavoriteItems(selectedProducts),
          folderId,
        });

        toast.success(t('searchProductsAddedToFavoritesSuccess', { folderName }), {
          autoClose: 3000,
        });
        clearSelections();
        if (isProductPanelOpen && selectedProduct) {
          await Promise.all([fetchProductDetails(), fetchProducts()]);
        } else {
          await fetchProducts();
        }
      } catch (error: unknown) {
        toast.error(getFavoritesErrorMessage(error));
      }
    },
    [
      productSelections,
      isProductPanelOpen,
      selectedProduct,
      products,
      folderOptions,
      addToFavorites,
      clearSelections,
      fetchProducts,
      fetchProductDetails,
      t,
    ]
  );

  const value = useMemo<SearchProductSelectionsContextShape>(
    () => ({
      productSelections,
      setProductSelections,
      onSelectionChange,
      onItemChange,
      getProductSelection,
      updateProductSelection,
      hasSelectedItems,
      clearSelections,
      folderOptions,
      handleAddToFavorites,
      isAddingToFavorites,
      handleSelectedItemsAddToCart,
      handleSingleItemAddToCart,
      isUpdatingCart,
      handleConfirm,
      handleRefineSearchChange,
      handleViewChange,
      onDataStateChange,
    }),
    [
      productSelections,
      onSelectionChange,
      onItemChange,
      getProductSelection,
      updateProductSelection,
      hasSelectedItems,
      clearSelections,
      folderOptions,
      handleAddToFavorites,
      isAddingToFavorites,
      handleSelectedItemsAddToCart,
      handleSingleItemAddToCart,
      isUpdatingCart,
      handleConfirm,
      handleRefineSearchChange,
      handleViewChange,
      onDataStateChange,
    ]
  );

  return (
    <SearchProductSelectionsContext.Provider value={value}>
      {children}
      <UnsavedSelectionDialog
        open={isUnsavedSelectionOpen}
        onClose={handleUnsavedClose}
        onConfirm={handleUnsavedClearAndConfirm}
        onAddToCart={handleUnsavedAddToCart}
        selectedItemsCount={selectedItemsCount}
      />
    </SearchProductSelectionsContext.Provider>
  );
};
